import { useCallback, useState } from "react";
import { isFailed, Step, Type } from "~/lib/pipeline";

interface UseExpandedGroupsOptions {
  steps: Step[];
  defaultExpandFailed?: boolean;
}

/**
 * Hook for managing expanded/collapsed state of group steps in the build canvas.
 */
export function useExpandedGroups({ steps, defaultExpandFailed = true }: UseExpandedGroupsOptions) {
  const [expandedGroupSteps, setExpandedGroupSteps] = useState<Set<string>>(() => {
    if (!defaultExpandFailed) {
      return new Set();
    }

    return new Set(
      steps.filter((step) => step.type === Type.Group && isFailed(step)).map((step) => step.uuid),
    );
  });

  const expandGroupStep = useCallback((stepId: string) => {
    setExpandedGroupSteps((prev) => new Set([...prev, stepId]));
  }, []);

  const collapseGroupStep = useCallback((stepId: string) => {
    setExpandedGroupSteps((prev) => {
      const next = new Set(prev);
      next.delete(stepId);
      return next;
    });
  }, []);

  const toggleGroupStep = useCallback((stepId: string) => {
    setExpandedGroupSteps((prev) => {
      const next = new Set(prev);
      if (next.has(stepId)) {
        next.delete(stepId);
      } else {
        next.add(stepId);
      }
      return next;
    });
  }, []);

  const expandAllGroups = useCallback(() => {
    const groupIds = steps.filter((step) => step.type === Type.Group).map((step) => step.uuid);
    setExpandedGroupSteps(new Set(groupIds));
  }, [steps]);

  const collapseAllGroups = useCallback(() => {
    setExpandedGroupSteps(new Set());
  }, []);

  return {
    expandedGroupSteps,
    expandGroupStep,
    collapseGroupStep,
    toggleGroupStep,
    expandAllGroups,
    collapseAllGroups,
  };
}
